import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty, find, includes } from 'lodash';
import { connect } from 'react-redux';
import { fetchBooking, resetBookingStore } from '../actions/booking';
import { updateProgress, resetProgress } from '../actions/flow';
import { fetchContractsSnapshot, cancelBookingQuote, resetContractStore } from '../actions/contracts';
import { CancelBookingMain } from '../../app-bookings-cancel/cancel-booking-main';
import { Spinner } from 'shared-components/decorators/spinner';
import { formValueSelector } from 'redux-form';
import { CANCEL_BOOKING_FORM } from '../components/reduxForms/constants/formNames';
import { CancelBookingOptions } from '../../app-bookings-cancel/cancel-booking-options';
import { verifyPSCredits } from '../../app-bookings-request/forms/bookingUtil';
import { BookingStates } from '../../../../constants/booking-states';
import { useStatsigContext } from '../../elements-statsig-provider/utils';

class CancelBookingContainer extends Component {
  constructor (props) {
    super(props);

    this.state = {
      displayOptionsPage: false
    };

    this.selectCancelAction = this.selectCancelAction.bind(this);
  }

  componentDidMount () {
    if (this.props.accessToken) {
      const { dispatch, routeParams, accessToken, iosBookingId } = this.props;
      // iosBookingID from props passed through in iOS view
      const bookingID = get(routeParams, 'id') || iosBookingId;
      dispatch(fetchBooking(bookingID, accessToken));
      dispatch(fetchContractsSnapshot(bookingID, accessToken));

      const quotePayload = {
        reason: '',
        private_reason: ''
      };
      dispatch(cancelBookingQuote(quotePayload, bookingID, accessToken, (response) => {
        const spaceRentalContract = find(response.contracts, { type: 'SPACE_RENTAL' });
        const cancelDetails = get(spaceRentalContract, 'meta.cancel_details', {});
        // cancel_charge_multiplier is how BE determines refund amounts (0 is full refund, 0.5 is half)
        const nonRefundable = 1 === cancelDetails.cancel_charge_multiplier;
        const cashRefund = get(response, 'payment_changes.refund_amount', 0);
        const creditRefund = get(response, 'payment_changes.credit_payment_amount', 0);
        const listing = get(spaceRentalContract, 'meta.listing', {});
        const psCreditsAllowed = verifyPSCredits(listing);

        // show credit refund form if payment was 100% credit & at least 50% refunable
        if (!!creditRefund && !cashRefund && !nonRefundable) {
          return dispatch(updateProgress('creditRefund'));
        }
        // show options page if payment was 100% cash & at least 50% refundable & ps credits are allowed
        if (!creditRefund && !!cashRefund && !nonRefundable && psCreditsAllowed) {
          return this.setState({ displayOptionsPage: true }, ()=> {
            dispatch(updateProgress('refundOptions'));
          });
        }
        // otherwise show standard refund form
        return dispatch(updateProgress('standardRefund'));
      }));
    }
  }

  componentWillReceiveProps (nextProps) {
    const { accessToken, profile, booking, ssoID } = nextProps;
    const bookingGuestID = get(booking, 'booking.guest_id');

    // if user logs out, redirect to sign in
    if (!accessToken) {
      window.location.href = '/';
    }
    // only booking guest can access cancel form

    const bookingState = get(booking, 'booking.obj_state', '');
    const isCanceled = includes([BookingStates.CANCELLED], bookingState);
    const isBookingGuest = bookingGuestID && (bookingGuestID !== (profile.SSOid || ssoID));

    if (isBookingGuest || isCanceled) {
      window.location.href = '/inbox';
    }
  }

  componentWillUnmount () {
    const { dispatch } = this.props;

    dispatch(resetContractStore());
    dispatch(resetBookingStore());
    dispatch(resetProgress());
  }

  selectCancelAction (cancelForCredit) {
    const { dispatch, scrollTopIOS, booking, accessToken } = this.props;
    const bookingId = get(booking, 'booking._id', '');

    const quotePayload = {
      reason: '',
      private_reason: '',
      credits_requested: cancelForCredit
    };
    dispatch(cancelBookingQuote(quotePayload, bookingId, accessToken));

    if (scrollTopIOS) {
      scrollTopIOS();
    }
    window.scrollTo(0, 0);
    return dispatch(updateProgress(cancelForCredit ? 'creditRefund' : 'standardRefund'));
  }

  render () {
    const { displayOptionsPage } = this.state;
    const {
      isFetching,
      iosBookingId,
      flowStep,
      contract,
      booking,
      allowCancelForCredits
    } = this.props;
    const updatedBooking = get(this.props, 'booking.booking._id') === (get(this.props, 'params.id') || iosBookingId);

    const displayedStep = allowCancelForCredits ? flowStep : 'standardRefund';

    const displayRefundOptions = 'refundOptions' === displayedStep;
    const displayCancelForm =
      'standardRefund' === displayedStep || 'creditRefund' === displayedStep;

    return (
      <div className="CancelBookingContainer">
        { (isFetching || !updatedBooking || isEmpty(contract.data) || isEmpty(contract.cancelContractObj) || isEmpty(booking.booking) || !flowStep)
          ?
          <div className="spinner-container">
            <Spinner additionalStyle="gray" />
          </div>
          : displayRefundOptions ?
            <CancelBookingOptions
              {...this.props}
              selectCancelAction={this.selectCancelAction}
            />
            : displayCancelForm ?
              <CancelBookingMain
                {...this.props}
                displayOptionsPage={displayOptionsPage}
                selectCancelAction={this.selectCancelAction}
              />
              : null
        }
      </div>
    );
  }
}

CancelBookingContainer.propTypes = {
  dispatch: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  isFetching: PropTypes.bool.isRequired,
  accessToken: PropTypes.string.isRequired,
  routeParams: PropTypes.object.isRequired,
  flowStep: PropTypes.string.isRequired,
  booking: PropTypes.object,
  contracts: PropTypes.object,
  bookingID: PropTypes.string
};

const selector = formValueSelector(CANCEL_BOOKING_FORM);

function mapStateToProps (state) {
  const { auth, profile, booking, contract, flow } = state;
  const contracts = get(contract, 'data.contracts');
  const cancelReasonValue = selector(state, 'cancelReason');
  const cancelContractObj = get(contract, 'cancelContractObj', {});
  const spaceRentalCancelContract = find(cancelContractObj.contracts, { type: 'SPACE_RENTAL' });
  const cancelDetails = get(spaceRentalCancelContract, 'meta.cancel_details', {});
  const contractsError = get(contract, 'error') ? 'Something went wrong, please try again.' : '';

  const {
    isFetching
  } = profile || {
    isFetching: true
  };

  const {
    isPosting
  } = contract || {
    isPosting: true
  };

  return {
    auth,
    profile,
    isFetching,
    isPosting,
    booking,
    contract,
    contracts,
    cancelDetails,
    cancelContractObj,
    contractsError,
    initialValues: {
      cancelReason: ''
    },
    cancelReasonValue,
    flowStep: get(flow, 'step', '')
  };
}

export default connect(mapStateToProps)((props) => {
  const { featureGates } = useStatsigContext();

  return (
    <CancelBookingContainer
      allowCancelForCredits={featureGates?.cancel_for_credits?.()}
      {...props}
    />
  );
});
